import React, { useState, useEffect } from 'react'
import { XIcon, LinkIcon, CheckIcon, Share2Icon } from 'lucide-react'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  videoUrl: string
  requestId: string
  aspectRatio?: string
  user?: any
}

import googleButton from '@/assets/google_button.svg';

const apiUrl = import.meta.env.VITE_API_BASE_URL;
const shareBaseUrl = import.meta.env.VITE_SHARE_BASE_URL;

export const ShareModal: React.FC<ShareModalProps> = ({
  requestId,
  aspectRatio,
  isOpen,
  onClose,
  videoUrl,
  user
}) => {
  const [copied, setCopied] = useState(false)
  const [shareMessage, setShareMessage] = useState("")
  const [downloading, setDownloading] = useState(false)
  const shareUrl = `${shareBaseUrl}/videos/${requestId}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

   const handleDownload = async () => {
    try {
      setDownloading(true)
      setShareMessage("Downloading video...")
      
      // Create a temporary anchor element
      const a = document.createElement('a')
      a.href = videoUrl
      a.download = `grapegraph-video-${requestId}.mp4`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      
      // Reset the UI after a delay
      setTimeout(() => {
        setDownloading(false)
        setShareMessage("Video downloaded successfully!")
        setTimeout(() => setShareMessage(""), 3000)
      }, 1500)
    } catch (err) {
      console.error('Failed to download video: ', err)
      setDownloading(false)
      setShareMessage("Failed to download video. Please try again.")
      setTimeout(() => setShareMessage(""), 3000)
    }
  }

  // Special handling for TikTok and Instagram
  const handleSpecialShare = async (platform: string) => {
    try {
      // First copy the URL to clipboard
      await navigator.clipboard.writeText(shareUrl)
      
      // Set a message to notify user
      setShareMessage(`URL copied! Opening ${platform}...`)
      
      // Open the respective app
      let appUrl = '';
      switch (platform) {
        case 'TikTok':
          appUrl = 'https://www.tiktok.com/'
          break
        case 'Instagram':
          appUrl = 'instagram://'
          break
        default:
          appUrl = ''
      }
      
      // Open the app after a short delay
      setTimeout(() => {
        window.open(appUrl, '_blank')
        // Clear message after 3 seconds
        setTimeout(() => setShareMessage(''), 3000)
      }, 500)
    } catch (err) {
      console.error(`Failed to share to ${platform}: `, err)
      setShareMessage(`Failed to share to ${platform}`)
      setTimeout(() => setShareMessage(''), 3000)
    }
  }

  useEffect(() => {
    const checkLoginStatus = async () => {
      // First check the simple cookie
      const isLoggedInCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_grape_graph_user_logged_in='));
      
      if (isLoggedInCookie) {
        try {
          // If the cookie exists, fetch the user details
          const response = await fetch(`${apiUrl}/api/auth_status`, {
            credentials: 'include' // Important: sends cookies with the request
          });

        } catch (error) {
          console.error('Error fetching auth status:', error);
        }
      }
    };
    
    checkLoginStatus();
  }, []);

  const handleSignup = () => {
    const url = `${apiUrl}/users/register?request_id=${requestId}`
    window.open(url, '_blank')
  }

  const handleSignupGoogle = () => {
    const url = `${apiUrl}/auth/google?request_id=${requestId}`
    window.open(url, '_blank')
  }

  // Social share handlers
  const handleShare = (platform: string) => {
    let shareLink = '';
    const encodedUrl = encodeURIComponent(shareUrl);
    const shareText = encodeURIComponent("Check out this video I created!");
    
    switch (platform) {
      case 'X':
        shareLink = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${shareText}`;
        break;
      case 'Facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'WhatsApp':
        shareLink = `https://wa.me/?text=${encodeURIComponent(`Check out this video I created! ${shareUrl}`)}`;
        break;
      case 'Signal':
        // Signal doesn't have a direct web intent
        // We'll copy the text and let users paste it themselves
        navigator.clipboard.writeText(`Check out this video I created! ${shareUrl}`);
        setShareMessage('Link copied! Open Signal and paste the link');
        setTimeout(() => setShareMessage(''), 3000);
        return;
      case 'TikTok':
      case 'Instagram':
        // Special handling for these platforms
        handleSpecialShare(platform);
        return;
      default:
        return;
    }
    
    window.open(shareLink, '_blank');
  }

  if (!isOpen) return null

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="bg-purple-50 p-2 rounded-lg">
                  <LinkIcon className="text-purple-600" size={20} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Sign up to share
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="mb-8 text-gray-900">Signing up is free and allows you to download and share the videos you create.</p>

              <div className="flex items-center justify-center gap-3 mb-4">
              <button onClick={handleSignupGoogle} className="cursor-pointer">
                <img src={googleButton} alt="Sign in with Google" className="w-full h-auto" />
              </button>

              <button
                onClick={handleSignup}
                className="cursor-pointer px-5 py-2.5 rounded-full text-sm transition-all flex items-center gap-2 bg-purple-600 text-white hover:bg-purple-700"
              >
                  Continue with email
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-2 rounded-lg">
                <LinkIcon className="text-purple-600" size={20} />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Share Video
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <XIcon size={20} />
            </button>
          </div>
          <div className="p-6">
            {/* Video Preview */}
            <div className="bg-black rounded-lg overflow-hidden mb-6">
              <div className="aspect-video">
                <video
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  controls
                />
              </div>
            </div>
            
            {/* Social Sharing Buttons */}
            <div className="space-y-4 mb-6">
              <label className="block text-sm font-medium text-gray-700">
                Share on social media
              </label>
              
              <div className="flex flex-wrap gap-3 justify-center mb-4">
                {/* X (Twitter) */}
                <button 
                  onClick={() => handleShare('X')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Share on X"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M13.6 10.4l5.6-6.5h-1.3L12.8 9.6 8.5 3.9H3.7l6.5 9-5.9 6.8h1.3l5.3-6.1 4.5 6.1h4.8l-6.6-9.3zM9.1 17.7l-1-1.4-7.5-10.7h2.3l6.1 8.7 1 1.4 7.8 11.2h-2.3l-6.4-9.2z" />
                  </svg>
                </button>
                
                {/* Facebook */}
                <button 
                  onClick={() => handleShare('Facebook')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                  aria-label="Share on Facebook"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </button>
                
                {/* WhatsApp */}
                <button 
                  onClick={() => handleShare('WhatsApp')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                  aria-label="Share on WhatsApp"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </button>
                
                {/* Signal */}
                <button 
                  onClick={() => handleShare('Signal')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                  aria-label="Share on Signal"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M12.012 2c-5.518 0-9.997 4.48-9.997 9.997 0 5.518 4.48 9.997 9.997 9.997 5.518 0 9.997-4.48 9.997-9.997 0-5.518-4.48-9.997-9.997-9.997zm0 1.5c4.69 0 8.497 3.808 8.497 8.497s-3.807 8.497-8.497 8.497-8.497-3.807-8.497-8.497S7.323 3.5 12.012 3.5zm-1.28 3.086a.75.75 0 00-.72.779v6.17a.75.75 0 001.493.104l.007-.103v-6.17a.75.75 0 00-.78-.78zm2.561 0a.75.75 0 00-.721.779v6.17a.75.75 0 001.493.104l.007-.103v-6.17a.75.75 0 00-.779-.78zm-5.129 1.602a.75.75 0 00-.687.81l.012.103.595 3.81a.75.75 0 001.493-.105l-.007-.103-.595-3.809a.75.75 0 00-.81-.706zm7.695 0a.75.75 0 00-.697.706l-.007.103-.595 3.809a.75.75 0 001.399.504l.007-.103.595-3.81a.75.75 0 00-.702-.81z" />
                  </svg>
                </button>
                
                {/* TikTok */}
                <button 
                  onClick={() => handleShare('TikTok')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Share on TikTok"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M19.321 5.562a5.124 5.124 0 01-3.503-1.38 5.124 5.124 0 01-1.506-3.624h-3.97v13.921c0 1.986-1.611 3.597-3.597 3.597a3.597 3.597 0 010-7.194c.226 0 .445.024.66.061v-3.972a7.55 7.55 0 00-.66-.033 7.57 7.57 0 00-7.57 7.57 7.57 7.57 0 007.57 7.57 7.57 7.57 0 007.57-7.57V8.925a8.8 8.8 0 005.007 1.564V6.522a5.166 5.166 0 01-5.007-.96z" />
                  </svg>
                </button>
                
                {/* Instagram */}
                <button 
                  onClick={() => handleShare('Instagram')}
                  className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-100 text-pink-600 hover:bg-pink-200 transition-colors"
                  aria-label="Share on Instagram"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Share notification message */}
            {shareMessage && (
              <div className="bg-blue-50 text-blue-700 p-3 rounded-lg mb-6 text-center">
                {shareMessage}
              </div>
            )}
            
            {/* Share Link */}
            <div className="space-y-4 mb-6">
              <label className="block text-sm font-medium text-gray-700">
                Share link
              </label>
              <div className="flex gap-2">
                <div className="flex-1 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 text-gray-600 text-sm truncate">
                  {shareUrl}
                </div>
                <button
                  onClick={handleCopy}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${copied ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                >
                  {copied ? (
                    <>
                      <CheckIcon size={18} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <LinkIcon size={18} />
                      Copy
                    </>
                  )}
                </button>

                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${downloading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {downloading ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download
                    </>
                  )}
                </button>

              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
  )
}
